import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { authFetch } from '../../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Parsing minimaliste du user-agent → { device, browser, os }.
function parseUA(ua) {
  if (!ua) return { browser: 'Inconnu', os: '', icon: 'bi-question-circle' };
  const u = ua.toLowerCase();
  let os = '';
  if (u.includes('windows')) os = 'Windows';
  else if (u.includes('iphone') || u.includes('ipad') || u.includes('ios')) os = 'iOS';
  else if (u.includes('mac os') || u.includes('macintosh')) os = 'macOS';
  else if (u.includes('android')) os = 'Android';
  else if (u.includes('linux')) os = 'Linux';
  let browser = 'Navigateur';
  if (u.includes('edg/')) browser = 'Edge';
  else if (u.includes('chrome') && !u.includes('edg/')) browser = 'Chrome';
  else if (u.includes('firefox')) browser = 'Firefox';
  else if (u.includes('safari') && !u.includes('chrome')) browser = 'Safari';
  const isMobile = u.includes('mobile') || u.includes('android') || u.includes('iphone');
  const icon = isMobile ? 'bi-phone' : 'bi-laptop';
  return { browser, os, icon };
}

function formatDateFR(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

const REASON_LABELS = {
  superseded: 'Remplacée (nouvelle connexion)',
  logout: 'Déconnexion',
  logout_impersonation: 'Fin d\'impersonation',
  inactivity: 'Expirée (inactivité)',
  revoked: 'Révoquée',
};

/**
 * « Mes connexions récentes » — RGPD art. 15 / transparence du compte.
 * Liste les 20 dernières sessions + révocation. GeoIP (ville) reporté.
 */
const RecentSessionsSection = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_URL}/api/auth/me/sessions`);
      const data = await res.json();
      if (res.ok) setSessions(data.sessions || []);
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async (session) => {
    setRevoking(session.session_id);
    try {
      const res = await authFetch(`${API_URL}/api/auth/me/sessions/${session.session_id}/revoke`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        if (data.was_current) {
          // On vient de couper notre propre session → déconnexion réelle.
          toast.success('Session révoquée. Vous allez être déconnecté.');
          try { localStorage.removeItem('token'); localStorage.removeItem('user'); } catch { /* noop */ }
          setTimeout(() => { window.location.href = '/login'; }, 800);
          return;
        }
        toast.success('Session révoquée.');
        load();
      } else {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Erreur lors de la révocation.');
      }
    } catch {
      toast.error('Erreur de connexion au serveur.');
    } finally {
      setRevoking(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mt-6" data-testid="recent-sessions-section">
      <h2 className="text-xl font-semibold mb-1 text-gray-900 flex items-center gap-2">
        <i className="bi bi-clock-history"></i>
        Mes connexions récentes
      </h2>
      <p className="text-sm text-gray-500 mb-6">
        Historique de vos 20 dernières sessions. Révoquez toute session que vous ne reconnaissez pas.
      </p>

      {loading ? (
        <div className="text-gray-500 text-sm py-8 text-center" data-testid="sessions-loading">
          <i className="bi bi-arrow-repeat animate-spin mr-2"></i>Chargement…
        </div>
      ) : sessions.length === 0 ? (
        <div className="text-gray-500 text-sm py-8 text-center" data-testid="sessions-empty">Aucune session enregistrée.</div>
      ) : (
        <div className="overflow-x-auto scrollbar-hide">
          <table className="w-full text-sm" data-testid="sessions-table">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-4 font-medium">Appareil</th>
                <th className="py-2 pr-4 font-medium">Adresse IP</th>
                <th className="py-2 pr-4 font-medium">Connexion</th>
                <th className="py-2 pr-4 font-medium">Statut</th>
                <th className="py-2 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => {
                const ua = parseUA(s.user_agent);
                return (
                  <tr key={s.id || s.session_id} className="border-b last:border-0" data-testid={`session-row-${s.session_id}`}>
                    <td className="py-3 pr-4 text-gray-900">
                      <span className="inline-flex items-center gap-2">
                        <i className={`bi ${ua.icon} text-gray-400`}></i>
                        <span>{ua.browser}{ua.os ? ` · ${ua.os}` : ''}</span>
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-gray-600 font-mono text-xs">{s.ip || '—'}</td>
                    <td className="py-3 pr-4 text-gray-600">{formatDateFR(s.login_at)}</td>
                    <td className="py-3 pr-4">
                      {s.active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-50 text-[#28A745] font-medium">
                          <i className="bi bi-circle-fill text-[8px]"></i>
                          {s.is_current ? 'Cette session' : 'Active'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">
                          {REASON_LABELS[s.end_reason] || 'Terminée'}
                        </span>
                      )}
                    </td>
                    <td className="py-3 text-right">
                      {s.active ? (
                        <button
                          onClick={() => revoke(s)}
                          disabled={revoking === s.session_id}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 text-[#DC2626] hover:bg-red-50 transition-colors disabled:opacity-50"
                          data-testid={`session-revoke-${s.session_id}`}
                        >
                          {revoking === s.session_id ? (
                            <><i className="bi bi-arrow-repeat animate-spin mr-1"></i>…</>
                          ) : (
                            <><i className="bi bi-x-circle mr-1"></i>{s.is_current ? 'Se déconnecter' : 'Révoquer'}</>
                          )}
                        </button>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RecentSessionsSection;
